import i18n from "../i18n";
import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import moment from "moment";
import config from "../config";
import { bbqIcon } from "../utils/bbqTerms";
import "./ClientsHistory.css";

const tr = (...args) => i18n.t(...args);


const renderStars = (n) => {
  const x = Math.max(0, Math.min(5, Number(n || 0)));
  const FULL = "\u2605";
  const EMPTY = "\u2606";
  return FULL.repeat(x) + EMPTY.repeat(5 - x);
};

const CalendarIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <path fill="currentColor" d="M7 2h2v2h6V2h2v2h3a2 2 0 0 1 2 2v13a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V6a2 2 0 0 1 2-2h3V2Zm13 8H4v9a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-9ZM4 8h16V6H4v2Z"/>
  </svg>
);

const ClockIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <path fill="currentColor" d="M12 2a10 10 0 1 1 0 20a10 10 0 0 1 0-20Zm1 5h-2v6l5 3l1-1.73l-4-2.27V7Z"/>
  </svg>
);

const PeopleIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <path fill="currentColor" d="M16 11a4 4 0 1 0 0-8a4 4 0 0 0 0 8Zm-8 0a4 4 0 1 0 0-8a4 4 0 0 0 0 8Zm0 2c-3.33 0-6 1.79-6 4v2h12v-2c0-2.21-2.67-4-6-4Zm8 0c-.29 0-.62.02-.97.07A5.65 5.65 0 0 1 18 17v2h4v-2c0-2.21-2.67-4-6-4Z"/>
  </svg>
);

const TableIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <path fill="currentColor" d="M3 7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v3H3V7Zm0 5h8v7H9v-4H5v4H3v-7Zm10 0h8v7h-2v-4h-4v4h-2v-7Z"/>
  </svg>
);

const MeatIcon = ({ size = 13 }) => (
  <span style={{ fontSize: size, lineHeight: 1 }} aria-hidden="true">{bbqIcon()}</span>
);

const ServedIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <path fill="currentColor" d="m9 16.17l-3.88-3.88L3.71 13.7L9 19l12-12l-1.41-1.41z"/>
  </svg>
);

const CommentIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <path fill="currentColor" d="M4 4h16a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H8l-4 4V6a2 2 0 0 1 2-2Z"/>
  </svg>
);

const CancelIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <path fill="currentColor" d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2Zm5 11H7v-2h10Z"/>
  </svg>
);

const getFeedback = (r) => {
  if (!r || typeof r !== "object") return null;

  const org = Number(r.feedback_org_service || 0);
  const qc = Number(r.feedback_quality_cost || 0);
  const env = Number(r.feedback_environment_clean || 0);
  const cmt = (r.feedback_comment || "").trim();

  const hasAny = org > 0 || qc > 0 || env > 0 || !!cmt;
  if (!hasAny) return null;

  return { org, qc, env, cmt };
};

const formatDatePart = (value) => moment(value).format("DD/MM/YY");
const formatTimePart = (value) => moment(value).format("HH:mm");

const normalizeTables = (value) => {
  const parts = String(value || "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);

  const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });
  return Array.from(new Set(parts)).sort(collator.compare).join(", ");
};

export default function ClientsHistory({
  clientId,
  clientName = "",
  prefetch = false,
  open,
  onClose,
  onTotals,
  showInlineTrigger = false,
  showInlineTotals = false,
  // Γεμίζει το ψηλό modal (86vh) αφήνοντας χώρο για τίτλο/σύνολα — ήταν 200px
  // και η λίστα σκρολάριζε σε ένα μικρό παράθυρο ενώ το modal έχασκε άδειο.
  maxListHeight = 'calc(86vh - 170px)',
}) {
  const [data, setData] = useState(null);
  const [internalOpen, setInternalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const isOpen = typeof open === "boolean" ? open : internalOpen;

  const close = useCallback(() => {
    if (typeof open === "boolean") {
      onClose?.();
    } else {
      setInternalOpen(false);
    }
  }, [open, onClose]);

  const doFetch = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const res = await axios.get(
        tr("{{API_BASE_URL}}/clients/{{clientId}}/history", {
          API_BASE_URL: config.API_BASE_URL,
          clientId,
        }),
        { withCredentials: true, timeout: 15000 }
      );

      setData(res.data || null);

      const totals = res?.data?.totals || null;
      if (typeof onTotals === "function") onTotals(totals);
    } catch (e) {
      console.error("Failed to load client history", e);
      setData(null);
      if (typeof onTotals === "function") onTotals(null);
    } finally {
      setLoading(false);
    }
  }, [clientId, onTotals]);

  useEffect(() => {
    setData(null);
  }, [clientId]);

  useEffect(() => {
    if (!clientId) {
      setData(null);
      if (typeof onTotals === "function") onTotals(null);
      return;
    }
    if (prefetch) doFetch();
  }, [clientId, prefetch, doFetch, onTotals]);

  useEffect(() => {
    if (!clientId) return;
    if (!prefetch && isOpen && !data && !loading) doFetch();
  }, [clientId, isOpen, prefetch, data, loading, doFetch]);

  const items = useMemo(() => {
    const res = Array.isArray(data?.reservations) ? data.reservations : [];
    const canc = Array.isArray(data?.cancellations) ? data.cancellations : [];

    return [
      ...res.map((r) => ({
        kind: "active",
        id: r.id,
        arrival: r.arrival,
        people: r.people,
        tables: normalizeTables(r.tables),
        comment: r.comment,
        feedback: getFeedback(r),
      })),
      ...canc.map((c) => ({
        kind: "cancelled",
        id: tr("c-{{id}}", { id: c.id }),
        reservationId: c.reservation,
        arrival: c.arrival,
        people: c.people,
        tables: "",
        comment: c.comment,
        cancelled: c.cancelled,
        username: c.username,
        feedback: null,
      })),
    ].sort((a, b) => new Date(b.arrival) - new Date(a.arrival));
  }, [data]);

  const inlineTotalsNode = useMemo(() => {
    if (!data?.totals) return null;
    return (
      <>
        {" "}&#8226;
        <b title={tr("All reservations ever created")} style={{ color: "darkGreen" }}>
          {data.totals.reservations}
        </b>
        {"r "}&#8226;
        <b title={tr("User-cancelled (manual)")} style={{ color: "darkOrange" }}>
          {data.totals.cancellations}
        </b>
        {"c "}&#8226;
        <b title={tr("Auto no-show (system)")} style={{ color: "darkRed" }}>
          {data.totals.no_show}
        </b>
        {"ns"}
      </>
    );
  }, [data]);

  const assignedMeatsByReservation = useMemo(() => {
    const map = new Map();
    (Array.isArray(data?.assignedMeats) ? data.assignedMeats : []).forEach((m) => {
      const key = String(m.reservation);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(m);
    });
    return map;
  }, [data]);

  return (
    <>
      {(showInlineTrigger || showInlineTotals) && clientId && (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          {showInlineTrigger && (
            <button
              type="button"
              className="ch-inlineBtn"
              title={tr("See reservation history for this client")}
              onClick={() => {
                if (typeof open !== "boolean") setInternalOpen(true);
              }}
            >
              &#128220;
            </button>
          )}
          {showInlineTotals && inlineTotalsNode}
        </span>
      )}

      {isOpen && (
        <div className="modal-overlay" onClick={close}>
          <div className="modal-body" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" aria-label={tr("Close")} onClick={close}>
              ×
            </button>

            <h3 className="history-title">
              {tr("Reservation History")}
              {clientName ? (
                <>
                  : <i className="client-name">{clientName}</i>
                </>
              ) : null}
            </h3>

            <div className="ch-scroll" style={{ maxHeight: maxListHeight, overflowY: "auto" }}>
              {data?.totals && (
                <div className="ch-totals">
                  <b>{tr("Lifetime totals:")}</b>{" "}
                  <b title={tr("All reservations ever created")} style={{ color: "darkGreen" }}>
                    {data.totals.reservations}
                  </b>{" "}
                  {tr("reservations,")}
                  <b title={tr("User-cancelled (manual)")} style={{ color: "darkOrange", marginLeft: 6 }}>
                    {data.totals.cancellations}
                  </b>{" "}
                  {tr("cancellations,")}
                  <b title={tr("Auto no-show (system)")} style={{ color: "darkRed", marginLeft: 6 }}>
                    {data.totals.no_show}
                  </b>{" "}
                  {tr("no-shows")}
                </div>
              )}

              {loading && <div style={{ padding: 8 }}>{tr("Loading...")}</div>}

              {!loading && items.length === 0 && <p>{tr("No reservations history found.")}</p>}

              {!loading &&
                items.map((item, i) => {
                  const meats = assignedMeatsByReservation.get(String(item.id)) || [];
                  const reservationNo = item.kind === "cancelled" ? item.reservationId : item.id;

                  return (
                    <div
                      key={String(item.id)}
                      className={`history-item ${item.kind === "cancelled" ? "cancelled" : ""}`}
                    >
                      <div className="ch-entryHead">
                        <div className="ch-entryIndex">{i + 1})</div>
                        <div className="ch-entryMain">
                          <div className="ch-entryTopRow">
                            <div className="ch-entryPrimary">
                              <span className="ch-reservationNo">#{reservationNo}</span>
                            </div>
                          </div>

                          <div className="ch-metaRow">
                            <span className="ch-metaChip history-line" title={tr("Date")}> 
                              <CalendarIcon /> {formatDatePart(item.arrival)}
                            </span>
                            <span className="ch-metaChip history-line" title={tr("Time")}> 
                              <ClockIcon /> {formatTimePart(item.arrival)}
                            </span>
                            <span className="ch-metaChip history-line" title={tr("People")}> 
                              <PeopleIcon /> {item.people || 0}
                            </span>
                            {!!item.tables && (
                              <span className="ch-metaChip history-line" title={tr("Tables")}> 
                                <TableIcon /> {item.tables}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {item.kind === "cancelled" && item.cancelled && (
                        <div className="history-meta ch-statusMeta">
                          <CancelIcon />
                          <span>
                            {tr("cancelled on")} {moment(item.cancelled).format("DD/MM/YY HH:mm")} {tr("by")} {item.username || tr("the system")}
                          </span>
                        </div>
                      )}

                      {item.kind === "active" && meats.length > 0 && (
                        <ul className="ch-meatsList">
                          {meats.map((m, idx) => (
                            <li key={`${m.id || idx}-${m.source || 'live'}`} className="ch-meatRow">
                              <span className="ch-meatMain">
                                <MeatIcon />
                                <span>
                                  {m.meat} - {m.grams}{tr("g")} - {m.cooktype}
                                </span>
                              </span>
                              {m.source === "history" && (
                                <span className="ch-meatServed">
                                  <ServedIcon />
                                  <span>
                                    {tr("served")}
                                    {m.served_at ? ` ${moment(m.served_at).format("DD/MM/YY HH:mm")}` : ""}
                                  </span>
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}

                      {item.feedback && (
                        <div className="ch-feedback">
                          <div className="ch-feedback-title">{tr("Feedback")}</div>
                          <div className="ch-feedback-row">
                            <span className="ch-feedback-label">{tr("Managment & Service")}</span>
                            <span className="ch-feedback-stars">{renderStars(item.feedback.org)}</span>
                          </div>
                          <div className="ch-feedback-row">
                            <span className="ch-feedback-label">{tr("Quality / Cost")}</span>
                            <span className="ch-feedback-stars">{renderStars(item.feedback.qc)}</span>
                          </div>
                          <div className="ch-feedback-row">
                            <span className="ch-feedback-label">{tr("Environment & Cleanliness")}</span>
                            <span className="ch-feedback-stars">{renderStars(item.feedback.env)}</span>
                          </div>
                          {!!item.feedback.cmt && <div className="ch-feedback-comment">{item.feedback.cmt}</div>}
                        </div>
                      )}

                      {item.comment && (
                        <div className="history-meta ch-commentRow">
                          <CommentIcon />
                          <i>{item.comment}</i>
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
